import PropTypes from 'prop-types'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { arrayMove } from 'react-sortable-hoc'
import styled, { ThemeContext } from 'styled-components/macro'
import { useMediaLayout } from 'use-media'

import { ImageCategories, ImageSetTypeEnum } from 'config/enums'
import * as routes from 'config/routes'

import { media } from 'utilities/styled'

import BodyHeading from 'components/atoms/body-heading'
import Button from 'components/atoms/button'
import LoadingIndicator from 'components/atoms/loading-indicator'
import NewDropdownSelect, {
  propTypes as newDropdownSelectPropTypes,
} from 'components/molecules/new-dropdown-select'
import SelectFileButton from 'components/molecules/select-file-button'
import Typography from 'components/molecules/typography'
import { CameraSearchParamsEnum } from 'components/views/camera-view'

import MediaFormContextMenu from 'components/molecules/media-form-context-menu'
import MediaFormUploadMenu from 'components/molecules/media-form-upload-menu'
import GenericImageCarouselContainer from 'components/redux-containers/generic-image-carousel-container'
import { v4Api } from 'config/data'
import { JWT_STORAGEID } from 'redux/reducers/auth'
import storage from 'utilities/storage'
import toast from 'utilities/toast'
import { SortableGrid } from './media-form/grid'

const sortItems = (items, newSortOrder) => {
  // If the number length of items is not the same as the of newSortOrder
  // this function has been called after uploading a new asset and the
  // the items are freshly served from the backend with a correct sort
  // order so the newSortorder can be ignored
  if (!newSortOrder || items.length !== newSortOrder.length) {
    return items.sort((first, second) => first.position - second.position)
  }
  return items.sort((first, second) => {
    const firstPosition = newSortOrder.find(
      (item) => item.id === first.id,
    ).position
    const secondPosition = newSortOrder.find(
      (item) => item.id === second.id,
    ).position

    return firstPosition - secondPosition
  })
}

const Header = styled.div`
  display: flex;
  align-content: center;
  flex-direction: column;
  justify-content: space-between;

  ${media.tablet`
    display:grid;
    grid-template-areas: "header header"
    "left-aligned right-aligned";
  `}
`

const MainHeading = styled(BodyHeading)`
  grid-area: header;
  margin: 0;
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};
  width: ${({ $isDamageOverlayOpen }) =>
    $isDamageOverlayOpen ? '100%' : 'auto'};
  justify-content: ${({ $isDamageOverlayOpen }) =>
    $isDamageOverlayOpen ? 'end' : 'start'};

  ${media.tablet`
    flex-direction: row;
  `}

  ${media.desktop`
    margin-left: ${({ theme, $selectionMode }) =>
      $selectionMode ? 0 : theme.sizings.lvl2};
    order: 2;
  `}
`

const LeftAligned = styled.div`
  grid-area: left-aligned;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: start;
  width: 100%;

  ${media.tablet`
  flex-direction: row;
  margin-top: 0;
  width: auto;
`}
`

const StyledNewDropdownSelect = styled(NewDropdownSelect)`
  width: ${(props) => props.theme.sizeByFactor(32)};
  margin: ${({ theme }) => theme.sizings.lvl2} 0;
`

const DesktopAddButton = styled(SelectFileButton)`
  display: flex;
  margin-left: 0;
`

const RotateButton = styled(Button)`
  align-items: center;
  display: inline-flex;
  height: ${({ theme }) => theme.sizeByFactor(4.75)};
  justify-content: center;
  padding: ${({ theme }) => theme.sizings.lvl0};

  > * {
    font-size: 30px;
  }
`

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: ${({ $compact }) => ($compact ? '0 1 auto' : '1 1 auto')};
  width: ${({ $compact }) => ($compact ? 'auto' : '100%')};
  gap: ${({ theme }) => theme.sizings.lvl2};
  flex-wrap: wrap;
  min-width: 0;

  ${media.desktop`
    order: 1;
  `}
`

const SelectAllActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const PhotoActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizings.lvl2};
  flex-shrink: 0;
  min-height: ${({ theme }) => theme.sizeByFactor(4.75)};
`

const StyledHeader = styled(Header)`
  margin-bottom: 0;
`

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.sizeByFactor(1.5)};
  margin-top: ${({ theme, $selectionMode }) =>
    $selectionMode ? theme.sizeByFactor(1.5) : 0};
  gap: ${({ theme, $selectionMode }) =>
    $selectionMode ? theme.sizeByFactor(3) : 0};

  ${media.desktop`
    gap: ${({ theme }) => theme.sizings.lvl3};
    display: flex;
    justify-content: ${({ $selectionMode }) =>
      $selectionMode ? 'flex-end' : 'space-between'};
    margin-top: 0;
  `}
`

const EmptyState = styled(Typography)`
  margin: 0 0 ${({ theme }) => theme.sizings.lvl3};
`

export const IMAGE_FILE_EXTENSIONS = [
  '.jpg',
  '.JPG',
  '.jpeg',
  '.JPEG',
  '.png',
  '.PNG',
]
export const DOCUMENT_FILE_EXTENSIONS = [
  '.doc',
  '.docx',
  '.pdf',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
]

/**
 * The MediaForm is a multfunctional tool to help upload and manage media files.
 */
const MediaForm = ({
  carAssetsSort,
  className,
  carId,
  disabled,
  noCustomCamera,
  noZipDownload,
  onUpload,
  onChange,
  onSelect,
  onDelete,
  onSort,
  items,
  level,
  onRotate,
  single,
  fullWidth,
  title,
  type,
  allowImageAsDocumentUpload,
  showLabelsOnImages,
  uploadButtonText,
  exclusiveSelect,
  allowEmptySelect,
  itemsFilter,
  itemsFilterValue,
  customOptions,
  enableReinitialize,
  readOnly,
  isValuation,
  imageAspectRatio,
  expandImage,
  imageCarouselType,
  emptyStateText,
  validateFile,
  ...restProps
}) => {
  const history = useHistory()
  const newSortOrder =
    carAssetsSort && carAssetsSort.payload && carAssetsSort.payload.sortOrder
  const previousItems = useRef(items)
  const isMounted = useRef(true)
  const [itemsList, setItemsList] = useState(
    items ? sortItems(items, newSortOrder) : [],
  )
  const { t } = useTranslation()
  const [selectedImageIds, setSelectedImageIds] = useState(
    items && items.length
      ? items.filter((item) => item.selected).map((item) => item.id)
      : [],
  )
  const [sortingImageId, setSortingImageId] = useState(null)
  const theme = useContext(ThemeContext)
  const isMobile = useMediaLayout({ maxWidth: theme.metrics.desktop })
  const getUserMediaSupported = window.Modernizr.getusermedia
  const customCamera =
    isMobile && getUserMediaSupported && type !== 'documents' && !noCustomCamera

  const allowedFileExtensions =
    type === 'images'
      ? IMAGE_FILE_EXTENSIONS // Only image uploads
      : allowImageAsDocumentUpload
        ? [...IMAGE_FILE_EXTENSIONS, ...DOCUMENT_FILE_EXTENSIONS] // Allow photos as document uploads
        : DOCUMENT_FILE_EXTENSIONS // Only documents

  const isDamageOverlayOpen = useSelector((state) => {
    const isDamageOverlaySelected =
      state.ui.overlay?.id === 'opticalDamage' ||
      state.ui.overlay?.id === 'technicalDamage'
    return isDamageOverlaySelected
  })

  const carouselImages = itemsList.filter((item) => item.type === 'image')

  const jwt = storage.getValue(JWT_STORAGEID, undefined)
  const showSelectedActionsWithSelectButton = !!onSelect

  useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  useEffect(() => {
    if (
      items &&
      (previousItems.current.length !== items.length || // if items have been added
        previousItems.current.some((previousItem, prevIndex) => {
          // if items are different
          return previousItem.src !== items[prevIndex].src
        }))
    ) {
      previousItems.current = items
      setItemsList(sortItems(items))
      if (enableReinitialize) {
        setSelectedImageIds([])
      }
    }
  }, [items, enableReinitialize])

  useEffect(() => {
    if (onChange) {
      onChange(selectedImageIds)
    }
  }, [selectedImageIds, onChange])

  const buttonTitle =
    uploadButtonText || (type === 'images' ? t('addFotos') : t('addDocuments'))

  const handleSelectAll = () => {
    setSelectedImageIds(items.map((item) => item.id))
  }

  const handleDeselectAll = () => {
    setSelectedImageIds([])
  }

  const toggleSelection = (index) => {
    if (exclusiveSelect) {
      setSelectedImageIds([index])
      return
    }

    if (selectedImageIds.indexOf(index) === -1) {
      const newSelectedImageIds = [...selectedImageIds, index]
      setSelectedImageIds(newSelectedImageIds)
    } else {
      const newSelectedImageIds = selectedImageIds.filter(
        (selectedIndex) => selectedIndex !== index,
      )
      setSelectedImageIds(newSelectedImageIds)
    }
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setSortingImageId(null)

    if (oldIndex === newIndex) {
      return
    }
    const newItemsList = arrayMove(itemsList, oldIndex, newIndex)
    const newSortOrderData = newItemsList.map((item, index) => ({
      id: item.id,
      position: index,
    }))
    setItemsList(newItemsList)
    onSort(newSortOrderData)
  }
  const addButtonId = type === 'images' ? 'addFotos' : 'addDocs'

  const handleSortStart = ({ index }) => {
    setSortingImageId(index)
  }

  const handleDelete = () => {
    onDelete(selectedImageIds)
    setSelectedImageIds([])
  }

  const handleSelect = () => {
    const selectedIds = selectedImageIds
    const result = onSelect(selectedIds)

    if (result && typeof result.then === 'function') {
      result.then(() => {
        if (isMounted.current) {
          setSelectedImageIds([])
        }
      })
      return
    }

    setSelectedImageIds([])
  }

  const handleView = (url) => {
    window.open(url, '_blank')
  }

  const handleChange = async (formData) => {
    if (!formData || !onUpload) {
      return
    }

    if (validateFile) {
      const file =
        typeof formData.get === 'function' ? formData.get('file') : null
      const validationError = await validateFile(file)

      if (validationError) {
        toast.error(validationError)
        return
      }
    }

    onUpload(formData)
  }

  const handleRotate = (degrees) => {
    onRotate(selectedImageIds, degrees)
  }

  const handleCameraButtonClick = () => {
    let searchParamsObj = {
      [CameraSearchParamsEnum.CAR_ID]: carId,
      [CameraSearchParamsEnum.ASSETS_COUNT]: items ? items.length : 0,
      [CameraSearchParamsEnum.REFERRER_URL]: `${history.location.pathname}${history.location.search}${window.location.hash}`,
      [CameraSearchParamsEnum.REFERRER_IMAGE_CATEGORY]:
        window.location.hash.replace('#', ''), // Should be "original" or "intern"
    }

    if (isValuation) {
      searchParamsObj = {
        ...searchParamsObj,
        [CameraSearchParamsEnum.REFERRER_IMAGE_CATEGORY]:
          ImageCategories.INTERNAL, // Used in the Camera component
      }
    }

    const searchParams = new URLSearchParams(searchParamsObj)

    history.push({
      pathname: routes.CAMERA,
      search: searchParams.toString(),
    })
  }

  const handleDownloadZip = () => {
    if (!selectedImageIds || selectedImageIds.length === 0 || !jwt || !v4Api) {
      return
    }

    const params = new URLSearchParams()
    params.set('_token', jwt)
    params.set('ids', selectedImageIds.join(','))

    const downloadUrl = `${new URL(v4Api).origin}/api/download/carimages/zip?${params.toString()}`
    window.open(downloadUrl, '_blank')
  }

  if (!itemsList) {
    return <LoadingIndicator />
  }
  return (
    <>
      <div className={className} data-test-e2e="media-form" {...restProps}>
        {(title || !single || onDelete || onUpload) && (
          <StyledHeader data-test-e2e="header">
            <MainHeading
              level="2"
              styledAs="oldheading"
              data-test-e2e="heading"
            >
              {title && title}

              {!single &&
                `${itemsList.length} ${t('files', {
                  count: itemsList.length,
                })}`}
            </MainHeading>

            <LeftAligned>
              {itemsFilter && itemsFilterValue && (
                <StyledNewDropdownSelect
                  disabled={itemsFilter?.disabled}
                  label={itemsFilter.label}
                  onChange={(value) => {
                    setSelectedImageIds([])
                    itemsFilter.onChange(value)
                  }}
                  items={itemsFilter.filterOptions}
                  value={itemsFilterValue}
                  selectionRequired
                  id="items-filter"
                  data-test-e2e="items-filter"
                />
              )}
            </LeftAligned>
          </StyledHeader>
        )}

        {!readOnly && (
          <ButtonsWrapper $selectionMode={showSelectedActionsWithSelectButton}>
            <ActionsContainer
              data-test-e2e="actions-container"
              $isDamageOverlayOpen={isDamageOverlayOpen}
              $selectionMode={showSelectedActionsWithSelectButton}
            >
              {onSelect && (
                <>
                  <Button
                    disabled={
                      (!allowEmptySelect &&
                        (!selectedImageIds || selectedImageIds.length === 0)) ||
                      disabled
                    }
                    onClick={handleSelect}
                  >
                    {uploadButtonText}
                  </Button>
                </>
              )}
            </ActionsContainer>
            <ActionButtonsContainer
              $compact={showSelectedActionsWithSelectButton}
            >
              {/* Select and deselect all button container */}
              <SelectAllActionContainer>
                {items &&
                  !!items.length &&
                  (onDelete || onRotate || onSelect || onChange) &&
                  !exclusiveSelect && (
                    <>
                      {selectedImageIds.length === 0 ? (
                        <Button
                          level="option"
                          noPadding
                          onClick={handleSelectAll}
                          disabled={
                            selectedImageIds.length === items.length || disabled
                          }
                          text={t('selectAll')}
                        />
                      ) : (
                        <Button
                          level="option"
                          noPadding
                          onClick={handleDeselectAll}
                          disabled={disabled}
                          text={t('deselectAll')}
                        />
                      )}
                    </>
                  )}
              </SelectAllActionContainer>

              {/* Photo add, rotate, download, delete, etc. actions */}
              {!showSelectedActionsWithSelectButton && (
                <PhotoActionsContainer>
                  {selectedImageIds.length > 0 && (
                    <>
                      {type === 'images' && onRotate && (
                        <>
                          <RotateButton
                            text="&#10226;"
                            level="option"
                            disabled={disabled}
                            onClick={() => handleRotate('L90')}
                            data-test-e2e="button-rotate-left"
                          />
                          <RotateButton
                            text="&#10227;"
                            level="option"
                            disabled={disabled}
                            onClick={() => handleRotate('R90')}
                            data-test-e2e="button-rotate-right"
                          />
                        </>
                      )}
                      <MediaFormContextMenu
                        selectedImageIds={selectedImageIds}
                        onDelete={onDelete ? handleDelete : null}
                        onDownloadZip={noZipDownload ? null : handleDownloadZip}
                        customOptions={customOptions}
                        type={type}
                        disabled={disabled}
                      />
                    </>
                  )}

                  {/* Upload button logic */}
                  {onUpload && (
                    <>
                      {isMobile ? (
                        <MediaFormUploadMenu
                          allowedFileExtensions={allowedFileExtensions}
                          disabled={disabled}
                          existingAssetsCount={items ? items.length : 0}
                          addButtonId={addButtonId}
                          multiple={!single}
                          onChange={handleChange}
                          customCamera={customCamera}
                          onCameraClick={handleCameraButtonClick}
                          buttonTitle={buttonTitle}
                          data-test-e2e="button-add-photo-mobile"
                        />
                      ) : (
                        <DesktopAddButton
                          allowedFileExtensions={allowedFileExtensions}
                          disabled={disabled}
                          existingAssetsCount={items ? items.length : 0}
                          id={addButtonId}
                          onChange={handleChange}
                          level={level}
                          multiple={!single}
                          text={buttonTitle}
                          data-test-e2e="button-add-photo"
                        />
                      )}
                    </>
                  )}
                </PhotoActionsContainer>
              )}
            </ActionButtonsContainer>
          </ButtonsWrapper>
        )}
      </div>
      {!itemsList.length && emptyStateText ? (
        <EmptyState type="ExplanationParagraph">{emptyStateText}</EmptyState>
      ) : (
        <SortableGrid
          axis="xy"
          disabled={!onSort || type === 'documents' || disabled}
          clickDisabled={disabled}
          items={itemsList}
          showLabelsOnImages={showLabelsOnImages}
          onSelect={onDelete || onSelect || onChange}
          updateBeforeSortStart={handleSortStart}
          onSortEnd={onSortEnd}
          onView={handleView}
          pressDelay={isMobile ? 200 : 0}
          distance={isMobile ? 0 : 5}
          selectedImageIds={selectedImageIds}
          sortingImageId={sortingImageId}
          t={t}
          toggleSelection={toggleSelection}
          exclusiveSelect={exclusiveSelect}
          data-test-e2e="sortable-grid"
          fullWidth={fullWidth}
          imageAspectRatio={imageAspectRatio}
          expandImage={expandImage}
          imageCarouselType={imageCarouselType}
        />
      )}
      {expandImage && <GenericImageCarouselContainer images={carouselImages} />}
    </>
  )
}

export const propTypes = {
  carAssetsSort: PropTypes.object,
  className: PropTypes.string,
  carId: PropTypes.string,
  disabled: PropTypes.bool,
  /** Level of submit button */
  level: PropTypes.string,
  noCustomCamera: PropTypes.bool,
  /** Hides the "Download as .zip" action, which only works for car images */
  noZipDownload: PropTypes.bool,
  /** Allows callback to when an image is selected */
  onChange: PropTypes.func,
  /** Allows callback to upload new images */
  onUpload: PropTypes.func,
  /** Allows deletion callback of current images */
  onDelete: PropTypes.func,
  /** Allows selection callback of images when images selected and confirm button pressed */
  onSelect: PropTypes.func,
  /** Allows dragging and sorting of files in files array */
  onSort: PropTypes.func,
  items: PropTypes.array,
  onRotate: PropTypes.func,
  /**
   * Set this to true is you only want to upload a single file per upload:
   */
  single: PropTypes.bool,
  /**
   * Set this to true if the image should span the full width of the grid
   */
  fullWidth: PropTypes.bool,
  title: PropTypes.string,
  /**
   * Labels on images only need to be shown sometimes, not every time.
   */
  showLabelsOnImages: PropTypes.bool,
  type: PropTypes.oneOf(['images', 'documents']).isRequired,
  allowImageAsDocumentUpload: PropTypes.bool,
  uploadButtonText: PropTypes.string,
  /** Only allows one media element to be selected at a time */
  exclusiveSelect: PropTypes.bool,
  /** Allows submitting an empty selection, for flows where empty means clear all */
  allowEmptySelect: PropTypes.bool,
  /**
   * collated set of itemsFilter parameters:
   * NOTE: is dependent on itemsFilterValue to be used too.
   */
  itemsFilter: PropTypes.shape({
    onChange: newDropdownSelectPropTypes.onChange,
    label: newDropdownSelectPropTypes.label,
    filterOptions: newDropdownSelectPropTypes.items,
    disabled: newDropdownSelectPropTypes.disabled,
  }),
  /**
   * Kept itemsFilterValue out of itemsFilter, because it updates all the time:
   * NOTE: is dependent on itemsFilter to be set:
   */
  itemsFilterValue: PropTypes.string,
  customOptions: PropTypes.arrayOf(
    PropTypes.shape({
      handler: PropTypes.func.isRequired,
      buttonText: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }).isRequired,
  ),
  enableReinitialize: PropTypes.bool, // Reset the selection of images if the array of images changes?
  readOnly: PropTypes.bool,
  isValuation: PropTypes.bool,
  imageAspectRatio: PropTypes.number,
  expandImage: PropTypes.bool,
  imageCarouselType: PropTypes.oneOf(Object.values(ImageSetTypeEnum)),
  emptyStateText: PropTypes.string,
  /**
   * Called with each selected file before it is uploaded. Return a message to
   * reject the file, or nothing to let the upload through.
   */
  validateFile: PropTypes.func,
}

MediaForm.propTypes = propTypes

MediaForm.defaultProps = {
  carId: null,
  carAssetsSort: null,
  className: null,
  disabled: false,
  level: 'cta',
  noCustomCamera: false,
  noZipDownload: false,
  onUpload: undefined,
  onChange: null,
  onDelete: null,
  onSelect: null,
  onRotate: null,
  onSort: null,
  items: null,
  single: false,
  fullWidth: false,
  title: null,
  showLabelsOnImages: false,
  allowImageAsDocumentUpload: false,
  uploadButtonText: null,
  exclusiveSelect: false,
  allowEmptySelect: false,
  itemsFilter: null,
  itemsFilterValue: null,
  customOptions: [],
  enableReinitialize: false,
  readOnly: false,
  isValuation: false,
  imageAspectRatio: 4 / 3,
  expandImage: false,
  imageCarouselType: ImageSetTypeEnum.GENERIC,
  emptyStateText: null,
  validateFile: null,
}

export default MediaForm
